import { Injectable } from '@angular/core';
import { HttpRequestService } from './api/http-request.service';
import { RequestResponse } from './api/interfaces/request.interface';
import { Observable, BehaviorSubject, tap, catchError, of } from 'rxjs';

export interface Permission {
  action: string;
  code: string;
}

export interface PermissionsByModule {
  [module: string]: Permission[];
}

export interface UserPermissionsResponse {
  code: number;
  data: {
    permissions: PermissionsByModule;
    permissions_list: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class UserPermissionsService extends HttpRequestService {
  private url = "users";
  private permissions$ = new BehaviorSubject<UserPermissionsResponse | null>(null);
  private permissionsCache: UserPermissionsResponse | null = null;

  constructor() {
    super();
  }

  /**
   * Obtiene los permisos del usuario actual desde el backend
   */
  public getUserPermissions(): Observable<UserPermissionsResponse> {
    // Si ya tenemos los permisos en caché, devolverlos
    if (this.permissionsCache) {
      return of(this.permissionsCache);
    }

    return this.get<UserPermissionsResponse>(`${this.url}/permissions`).pipe(
      tap(response => {
        this.permissionsCache = response;
        this.permissions$.next(response);
        // Guardar en localStorage para persistencia
        localStorage.setItem('user-permissions', JSON.stringify(response));
      }),
      catchError(error => {
        console.error('Error obteniendo permisos:', error);
        // Intentar cargar desde localStorage si hay error
        const cached = localStorage.getItem('user-permissions');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            this.permissionsCache = parsed;
            this.permissions$.next(parsed);
            return of(parsed);
          } catch (e) {
            console.error('Error parseando permisos del cache:', e);
          }
        }
        return of({
          code: 200,
          data: {
            permissions: {},
            permissions_list: []
          }
        } as UserPermissionsResponse);
      })
    );
  }

  /**
   * Carga los permisos desde localStorage si existen
   */
  public loadPermissionsFromCache(): void {
    const cached = localStorage.getItem('user-permissions');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.permissionsCache = parsed;
        this.permissions$.next(parsed);
      } catch (e) {
        console.error('Error parseando permisos del cache:', e);
      }
    }
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param module - Módulo del permiso (ej: 'users', 'prospects')
   * @param action - Acción del permiso (ej: 'create', 'read', 'update', 'delete')
   * @returns true si el usuario tiene el permiso
   */
  public hasPermission(module: string, action: string): boolean {
    const code = `${module}:${action}`;
    const permissions = this.permissionsCache?.data?.permissions_list || [];
    return permissions.includes(code);
  }

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   * @param permissions - Array de objetos {module, action}
   * @returns true si tiene al menos uno de los permisos
   */
  public hasAnyPermission(permissions: Array<{module: string, action: string}>): boolean {
    return permissions.some(perm => this.hasPermission(perm.module, perm.action));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   * @param permissions - Array de objetos {module, action}
   * @returns true si tiene todos los permisos
   */
  public hasAllPermissions(permissions: Array<{module: string, action: string}>): boolean {
    return permissions.every(perm => this.hasPermission(perm.module, perm.action));
  }

  /**
   * Obtiene los permisos de un módulo específico
   * @param module - Nombre del módulo
   * @returns Array de acciones permitidas para el módulo
   */
  public getModulePermissions(module: string): string[] {
    const modulePerms = this.permissionsCache?.data?.permissions[module] || [];
    return modulePerms.map(p => p.action);
  }

  /**
   * Obtiene todos los permisos como Observable
   */
  public getPermissions$(): Observable<UserPermissionsResponse | null> {
    return this.permissions$.asObservable();
  }

  /**
   * Limpia los permisos (útil al cerrar sesión)
   */
  public clearPermissions(): void {
    this.permissionsCache = null;
    this.permissions$.next(null);
    localStorage.removeItem('user-permissions');
  }

  /**
   * Obtiene la lista completa de códigos de permisos
   */
  public getPermissionsList(): string[] {
    return this.permissionsCache?.data?.permissions_list || [];
  }
}

